import { useMemo, useState } from "react";
import { Check, AlertTriangle, HelpCircle, Quote, X, Sparkles } from "lucide-react";
import type { ExtractionResult } from "../../store/useFreelancerJobRecordsStore";

// ── What the conversation gave us, before any of it is applied ───────────────
//
// Shown as a proposal the BD accepts rather than a set of fields that have
// already been written. Three things it is careful about:
//
//   Each value is shown WITH the quote it came from, so "is this right" is
//   answerable without scrolling back through the thread.
//
//   Fields the BD has already filled in are not offered. Overwriting work
//   somebody did by hand with a machine's reading of a chat log is the one
//   thing this must never do on its own — those rows are listed separately
//   and need an explicit tick.
//
//   Conflicts and ambiguities are listed and NOT applied. The conversation
//   said two things; a BD decides which, not this.

interface Props {
  result: ExtractionResult;
  // Which of the proposed fields already hold a value in the form.
  occupied: (field: string) => boolean;
  onApply: (fields: string[], deliverables: boolean) => void;
  onDismiss: () => void;
}

const preview = (v: any) =>
  Array.isArray(v) ? v.join(", ") : typeof v === "object" && v !== null ? JSON.stringify(v) : String(v ?? "");

const ExtractionReview = ({ result, occupied, onApply, onDismiss }: Props) => {
  const entries = useMemo(() => Object.entries(result.fields || {}), [result.fields]);

  const fresh = entries.filter(([f]) => !occupied(f));
  const clashing = entries.filter(([f]) => occupied(f));

  // Everything that would not overwrite anything starts ticked. Anything that
  // would starts unticked, and stays that way unless the BD says otherwise.
  const [chosen, setChosen] = useState<Set<string>>(new Set(fresh.map(([f]) => f)));
  const [takeDeliverables, setTakeDeliverables] = useState(result.deliverables.length > 0);

  const toggle = (f: string) =>
    setChosen((s) => {
      const next = new Set(s);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });

  const nothingFound = entries.length === 0 && result.deliverables.length === 0;

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] font-bold text-on-surface flex items-center gap-1.5">
            <Sparkles size={14} className="text-primary" />
            Read from the conversation
          </p>
          <p className="text-[11.5px] text-on-surface-variant mt-0.5">
            Nothing is saved yet. Each value shows the line it came from — check it, then apply.
          </p>
        </div>
        <button type="button" onClick={onDismiss} className="p-1 text-on-surface-variant hover:bg-surface-container rounded-full shrink-0">
          <X size={16} />
        </button>
      </div>

      {nothingFound && (
        <p className="text-[12px] text-on-surface-variant">
          Nothing in this conversation could be traced to a field on this form. Fill them in by hand — the
          conversation stays where it is.
        </p>
      )}

      {/* Safe to apply — these fields are empty */}
      {fresh.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
            Found ({fresh.length})
          </p>
          {fresh.map(([field, v]) => (
            <label key={field} className="flex items-start gap-2 rounded-lg bg-surface-container-lowest border border-outline-variant p-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={chosen.has(field)}
                onChange={() => toggle(field)}
                className="mt-0.5 shrink-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-semibold text-on-surface">
                  {labelFor(result, field)}
                </span>
                <span className="block text-[12.5px] text-on-surface break-words">{preview(v.value)}</span>
                <span className="mt-1 flex items-start gap-1 text-[11px] text-on-surface-variant italic">
                  <Quote size={10} className="mt-0.5 shrink-0" />
                  {v.evidence}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Would overwrite something the BD already entered */}
      {clashing.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
            You have already filled these in ({clashing.length})
          </p>
          <p className="text-[11px] text-on-surface-variant">
            Left alone unless you tick them. Ticking replaces what you typed.
          </p>
          {clashing.map(([field, v]) => (
            <label key={field} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 cursor-pointer">
              <input type="checkbox" checked={chosen.has(field)} onChange={() => toggle(field)} className="mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-semibold text-on-surface">{labelFor(result, field)}</span>
                <span className="block text-[12.5px] text-on-surface break-words">{preview(v.value)}</span>
                <span className="mt-1 flex items-start gap-1 text-[11px] text-on-surface-variant italic">
                  <Quote size={10} className="mt-0.5 shrink-0" /> {v.evidence}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      {result.deliverables.length > 0 && (
        <label className="flex items-start gap-2 rounded-lg bg-surface-container-lowest border border-outline-variant p-2.5 cursor-pointer">
          <input type="checkbox" checked={takeDeliverables} onChange={(e) => setTakeDeliverables(e.target.checked)} className="mt-0.5 shrink-0" />
          <span className="min-w-0">
            <span className="block text-[12px] font-semibold text-on-surface">
              Deliverables ({result.deliverables.length})
            </span>
            <span className="block text-[12px] text-on-surface-variant">
              {result.deliverables.map((d) => d.title).join(" · ")}
            </span>
            <span className="block text-[11px] text-on-surface-variant mt-0.5">Added to what is already there.</span>
          </span>
        </label>
      )}

      {/* Reported, never applied */}
      {result.conflicts?.length > 0 && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-2.5">
          <p className="text-[12px] font-bold text-rose-900 flex items-center gap-1.5">
            <AlertTriangle size={12} /> The conversation says two different things
          </p>
          {result.conflicts.map((c, i) => (
            <div key={i} className="mt-1.5">
              <p className="text-[11.5px] font-semibold text-on-surface">{c.label || c.field}</p>
              {c.statements.map((s, j) => (
                <p key={j} className="text-[11px] text-on-surface-variant italic pl-3">— {s}</p>
              ))}
            </div>
          ))}
          <p className="text-[11px] text-rose-800 mt-1.5">Left blank on purpose. Decide which is right and enter it yourself.</p>
        </div>
      )}

      {result.ambiguous?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
          <p className="text-[12px] font-bold text-amber-900 flex items-center gap-1.5">
            <HelpCircle size={12} /> Mentioned, but not clear enough to use
          </p>
          {result.ambiguous.map((a, i) => (
            <div key={i} className="mt-1.5">
              <p className="text-[11.5px] text-on-surface">
                <span className="font-semibold">{a.label || a.field}</span> — {a.why}
              </p>
              <p className="text-[11px] text-on-surface-variant italic pl-3">— {a.evidence}</p>
            </div>
          ))}
        </div>
      )}

      {/* Said out loud so "it missed this" is distinguishable from "it made
          this up". Anything the model could not point at in the text was
          discarded, and the BD deserves to know it happened. */}
      {result.rejected?.length > 0 && (
        <details className="rounded-lg border border-outline-variant bg-surface-container-lowest p-2.5">
          <summary className="text-[11.5px] font-semibold text-on-surface-variant cursor-pointer">
            {result.rejected.length} value(s) were discarded
          </summary>
          <ul className="mt-1.5 space-y-1">
            {result.rejected.map((r, i) => (
              <li key={i} className="text-[11px] text-on-surface-variant">
                <span className="font-semibold text-on-surface">{r.label || r.field}</span>
                {r.claimed !== undefined && r.claimed !== null ? ` — "${preview(r.claimed).slice(0, 60)}"` : ""} · {r.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      {!nothingFound && (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => onApply([...chosen], takeDeliverables)}
            disabled={chosen.size === 0 && !takeDeliverables}
            className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
          >
            <Check size={13} />
            Apply {chosen.size + (takeDeliverables ? 1 : 0)} of {entries.length + (result.deliverables.length ? 1 : 0)}
          </button>
          <button type="button" onClick={onDismiss} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-on-surface-variant hover:bg-surface-container">
            Discard
          </button>
          {result.missing && result.missing.length > 0 && (
            <span className="text-[11px] text-on-surface-variant ml-auto">
              {result.missing.length} field(s) left for you
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// The server sends a label with conflicts and ambiguities; field rows carry
// only the path, so fall back to a readable form of it.
const labelFor = (result: ExtractionResult, field: string) => {
  const fromConflict = result.conflicts?.find((c) => c.field === field)?.label;
  if (fromConflict) return fromConflict;
  return field
    .replace(/\./g, " · ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
};

export default ExtractionReview;
